import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { playGame, getGameRewards, getPlaysRemaining, getGameStats } from "../lib/api";
import { Button } from "../components/ui/button";
import Dice from "../components/Dice";
import {
  ArrowDown,
  Equal,
  ArrowUp,
  Coins,
  TrendingUp,
  Trophy,
  Gamepad2,
  Sparkles,
  Clock,
  AlertCircle,
} from "lucide-react";

const predictionOptions = [
  {
    id: "under",
    label: "Under 7",
    description: "Total is 2-6",
    icon: ArrowDown,
    color: "primary",
  },
  {
    id: "exactly",
    label: "Exactly 7",
    description: "Total is 7",
    icon: Equal,
    color: "secondary",
  },
  {
    id: "over",
    label: "Over 7",
    description: "Total is 8-12",
    icon: ArrowUp,
    color: "accent",
  },
];

export default function Game() {
  const { user, updateUser } = useAuth();
  const [rewards, setRewards] = useState({ play: 1, under_win: 2, exactly_win: 4, over_win: 2, daily_limit: 5 });
  const [playsRemaining, setPlaysRemaining] = useState(5);
  const [prediction, setPrediction] = useState(null);
  const [isRolling, setIsRolling] = useState(false);
  const [result, setResult] = useState(null);
  const [stats, setStats] = useState(null);
  const [dice, setDice] = useState({ dice1: 1, dice2: 1 });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [rewardsRes, playsRes, statsRes] = await Promise.all([
        getGameRewards(),
        getPlaysRemaining(),
        getGameStats(),
      ]);
      setRewards(rewardsRes.data);
      setPlaysRemaining(playsRes.data.plays_remaining);
      setStats(statsRes.data);
    } catch (error) {
      console.error("Failed to load game data:", error);
    }
  };

  const handlePlay = async () => {
    if (!prediction) {
      toast.error("Select a prediction first!");
      return;
    }

    if (playsRemaining <= 0) {
      toast.error("No plays remaining today! Come back tomorrow.");
      return;
    }

    setIsRolling(true);
    setResult(null);

    // Animate dice with random values
    const rollInterval = setInterval(() => {
      setDice({
        dice1: Math.floor(Math.random() * 6) + 1,
        dice2: Math.floor(Math.random() * 6) + 1,
      });
    }, 100);

    try {
      const response = await playGame({ prediction });
      const gameResult = response.data;
      
      // Stop animation and show actual result
      setTimeout(() => {
        clearInterval(rollInterval);
        
        // Set final dice values from server
        setDice({
          dice1: gameResult.dice1,
          dice2: gameResult.dice2,
        });
        
        // Small delay before showing result to ensure dice are updated
        setTimeout(() => {
          setResult(gameResult);
          setIsRolling(false);
          setPlaysRemaining(gameResult.plays_remaining);
          updateUser({ pay_balance: gameResult.new_pay_balance });
          
          if (gameResult.won) {
            toast.success(`🎉 You won ${gameResult.pay_earned} PAY coins!`);
          } else {
            toast.info(`You earned ${gameResult.pay_earned} PAY for playing!`);
          }

          // Refresh stats
          getGameStats().then((res) => setStats(res.data));
        }, 100);
      }, 800);
    } catch (error) {
      clearInterval(rollInterval);
      setIsRolling(false);
      if (error.response?.status === 429) {
        toast.error(error.response.data.detail);
        setPlaysRemaining(0);
      } else {
        toast.error(error.response?.data?.detail || "Game error");
      }
    }
  };

  const getWinReward = (predictionType) => {
    return rewards[`${predictionType}_win`] || 2;
  };

  const canPlay = playsRemaining > 0;

  return (
    <div className="min-h-screen p-4 md:p-8">
      <div className="max-w-6xl mx-auto">
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Game Area */}
          <div className="lg:col-span-2 space-y-6">
            {/* Plays Remaining Banner */}
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`rounded-xl p-4 flex items-center justify-between ${
                canPlay 
                  ? "bg-secondary/10 border border-secondary/20" 
                  : "bg-destructive/10 border border-destructive/20"
              }`}
            >
              <div className="flex items-center gap-3">
                {canPlay ? (
                  <Gamepad2 className="w-5 h-5 text-secondary" />
                ) : (
                  <AlertCircle className="w-5 h-5 text-destructive" />
                )}
                <div>
                  <div className="font-heading font-bold">
                    {canPlay ? "Free Plays Available" : "Daily Limit Reached"}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {canPlay 
                      ? `${playsRemaining} of ${rewards.daily_limit} plays remaining today`
                      : "Come back tomorrow for more free plays!"
                    }
                  </div>
                </div>
              </div>
              <div className={`text-3xl font-heading font-bold ${canPlay ? "text-secondary" : "text-destructive"}`}>
                {playsRemaining}/{rewards.daily_limit}
              </div>
            </motion.div>

            {/* Dice Display */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="glass-card rounded-2xl p-8 text-center"
            >
              <div className="flex items-center justify-center gap-2 mb-2">
                <Gamepad2 className="w-6 h-6 text-secondary" />
                <h2 className="font-heading font-bold text-2xl">Lucky 7</h2>
              </div>
              <p className="text-muted-foreground text-sm mb-8">
                Predict the dice total and earn PAY coins!
              </p>
              
              <div className="mb-8">
                <Dice dice1={dice.dice1} dice2={dice.dice2} isRolling={isRolling} />
              </div>

              {/* Result Display */}
              <AnimatePresence>
                {result && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    className={`inline-flex items-center gap-3 px-6 py-3 rounded-xl ${
                      result.won
                        ? "bg-secondary/20 border border-secondary/50 text-secondary"
                        : "bg-primary/20 border border-primary/50 text-primary"
                    }`}
                    data-testid="game-result"
                  >
                    <span className="text-4xl font-heading font-bold">{result.total}</span>
                    <div className="text-left">
                      <div className="font-bold flex items-center gap-1">
                        {result.won ? (
                          <>
                            <Sparkles className="w-4 h-4" /> YOU WON!
                          </>
                        ) : (
                          "Thanks for playing!"
                        )}
                      </div>
                      <div className="text-sm opacity-80 flex items-center gap-1">
                        <Coins className="w-3 h-3" />
                        +{result.pay_earned} PAY
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Predictions */}
              <div className="grid grid-cols-3 gap-4 mt-8">
                {predictionOptions.map((option) => {
                  const Icon = option.icon;
                  const isSelected = prediction === option.id;
                  const winReward = getWinReward(option.id);

                  return (
                    <motion.button
                      key={option.id}
                      whileHover={{ scale: canPlay ? 1.02 : 1 }}
                      whileTap={{ scale: canPlay ? 0.98 : 1 }}
                      onClick={() => canPlay && setPrediction(option.id)}
                      disabled={isRolling || !canPlay}
                      className={`p-4 rounded-xl border-2 transition-all ${
                        !canPlay
                          ? "border-white/5 bg-card/50 opacity-50 cursor-not-allowed"
                          : isSelected
                          ? `border-${option.color} bg-${option.color}/20 neon-glow`
                          : "border-white/10 bg-card hover:border-white/20"
                      }`}
                      data-testid={`prediction-${option.id}`}
                    >
                      <Icon
                        className={`w-8 h-8 mx-auto mb-2 ${
                          isSelected ? `text-${option.color}` : "text-muted-foreground"
                        }`}
                      />
                      <div className="font-heading font-bold">{option.label}</div>
                      <div className="text-xs text-muted-foreground">{option.description}</div>
                      <div className={`text-lg font-mono font-bold mt-2 flex items-center justify-center gap-1 ${isSelected ? `text-${option.color}` : ""}`}>
                        <Coins className="w-4 h-4" />
                        +{winReward}
                      </div>
                    </motion.button>
                  );
                })}
              </div>

              {/* Play Button */}
              <Button
                onClick={handlePlay}
                disabled={isRolling || !prediction || !canPlay}
                size="lg"
                className={`w-full mt-8 h-14 font-heading font-bold text-xl uppercase tracking-wider ${
                  canPlay 
                    ? "bg-secondary hover:bg-secondary/90 btn-glow" 
                    : "bg-muted text-muted-foreground cursor-not-allowed"
                }`}
                data-testid="roll-dice-btn"
              >
                {!canPlay ? (
                  <div className="flex items-center gap-2">
                    <Clock className="w-5 h-5" /> No Plays Left Today
                  </div>
                ) : isRolling ? (
                  <div className="flex items-center gap-2">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Rolling...
                  </div>
                ) : (
                  <>
                    <Gamepad2 className="w-6 h-6 mr-2" /> Roll Dice
                  </>
                )}
              </Button>

              {/* Potential Win */}
              {prediction && canPlay && (
                <div className="mt-4 text-center text-muted-foreground">
                  Win:{" "}
                  <span className="text-secondary font-bold font-mono">
                    +{getWinReward(prediction)} PAY
                  </span>
                  <span className="mx-2">|</span>
                  Play bonus:{" "}
                  <span className="text-primary font-bold font-mono">
                    +{rewards.play} PAY
                  </span>
                </div>
              )}
            </motion.div>
          </div>

          {/* Side Panel */}
          <div className="space-y-6">
            {/* PAY Balance Card */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="glass-card rounded-xl p-6 border-secondary/30"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-secondary/20 flex items-center justify-center">
                  <Coins className="w-5 h-5 text-secondary" />
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">Your PAY Balance</div>
                  <div className="text-2xl font-mono font-bold text-secondary" data-testid="pay-balance-display">
                    {(user?.pay_balance || 0).toLocaleString()} PAY
                  </div>
                </div>
              </div>
              <Button
                variant="outline"
                className="w-full border-secondary/30 hover:bg-secondary/10 text-secondary"
                onClick={() => window.location.href = "/wallet"}
                data-testid="withdraw-btn"
              >
                Withdraw PAY
              </Button>
            </motion.div>

            {/* Stats Card */}
            {stats && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 }}
                className="glass-card rounded-xl p-6"
              >
                <h3 className="font-heading font-bold text-lg mb-4 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-primary" /> Your Stats
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Total Games</span>
                    <span className="font-mono font-bold">{stats.total_games}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Wins</span>
                    <span className="font-mono font-bold text-secondary">{stats.wins}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Losses</span>
                    <span className="font-mono font-bold text-muted-foreground">{stats.losses}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Win Rate</span>
                    <span className="font-mono font-bold">{stats.win_rate?.toFixed(1) || 0}%</span>
                  </div>
                  <div className="pt-3 border-t border-white/10">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Total PAY Earned</span>
                      <span className="font-mono font-bold text-secondary">
                        {stats.total_pay_earned || 0} PAY
                      </span>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Rewards Info */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="glass-card rounded-xl p-6"
            >
              <h3 className="font-heading font-bold text-lg mb-4 flex items-center gap-2">
                <Trophy className="w-5 h-5 text-secondary" /> PAY Rewards
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Play Bonus</span>
                  <span className="font-mono font-bold text-primary flex items-center gap-1">
                    <Coins className="w-3 h-3" /> +{rewards.play}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <ArrowDown className="w-4 h-4 text-primary" />
                    <span>Under 7 Win</span>
                  </div>
                  <span className="font-mono font-bold text-primary flex items-center gap-1">
                    <Coins className="w-3 h-3" /> +{rewards.under_win}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <Equal className="w-4 h-4 text-secondary" />
                    <span>Exactly 7 Win</span>
                  </div>
                  <span className="font-mono font-bold text-secondary flex items-center gap-1">
                    <Coins className="w-3 h-3" /> +{rewards.exactly_win}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <ArrowUp className="w-4 h-4 text-accent" />
                    <span>Over 7 Win</span>
                  </div>
                  <span className="font-mono font-bold text-accent flex items-center gap-1">
                    <Coins className="w-3 h-3" /> +{rewards.over_win}
                  </span>
                </div>
              </div>
              <div className="mt-4 p-3 rounded-lg bg-secondary/10 border border-secondary/20">
                <p className="text-xs text-muted-foreground">
                  <span className="text-secondary font-medium">{rewards.daily_limit} free plays daily!</span> Earn PAY coins with every roll. Win bonuses for correct predictions!
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}
